import { DataTable } from "@/components/ui/table";

import TablePagination from "@/components/common/TablePagination";

import { callLoader } from "@/redux/Loader/actions";

import {
  GetCompanyRequestList,
  updateCompanyRequestStatus,
} from "@/redux/Masters/company/actions";

import { Building2, Eye, Search, CheckCircle2, XCircle } from "lucide-react";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import ConfirmModal from "@/components/ui/CelebrationButton/ConfirmModal/ConfirmModal";
import { showToast } from "@/redux/Toast/actions";
import { formatDisplayDate } from "@/utils/dateUtils";
import ColumnVisibilityDropdown from "@/components/common/ColumnVisibilityDropdown";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

import { useNavigate } from "react-router-dom";
import TableText from "@/components/common/TableText";

const RequestCompanies = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { companyRequestList, companyRequestListPagination } = useSelector(
    (state) => state.masterCompany,
  );
  console.log("companyRequestList: ", companyRequestList);

  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);

  const [pageSize, setPageSize] = useState(10);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [actionType, setActionType] = useState("Confirmed");

  // =========================================
  // COLUMNS
  // =========================================

  const columns = [
    {
      key: "company",
      label: "Company",
      render: (row) => {
        const name = row.COMPANYNAME || "-";
        const logo = row.COMPANYLOGO;

        const initials = name
          .split(" ")
          .filter(Boolean)
          .slice(0, 2)
          .map((w) => w[0])
          .join("")
          .toUpperCase();

        const isValidLogo =
          logo &&
          !logo.includes("api.shipez.ai/") &&
          !logo.endsWith("/") &&
          logo.startsWith("http");

        return (
          <div
            onClick={() => handleViewCompany(row)}
            className="flex items-center gap-4 cursor-pointer group"
          >
            <Avatar className="w-11 h-11 rounded-xl">
              <AvatarImage
                src={isValidLogo ? logo : undefined}
                alt={name}
                className="object-cover"
              />

              <AvatarFallback className="bg-teal-500 text-white font-semibold">
                {initials || "C"}
              </AvatarFallback>
            </Avatar>

            <div>
              <p className="font-semibold text-sm text-slate-900 dark:text-slate-400 group-hover:text-blue-500 transition-colors">
                {name}
              </p>

              <p className="text-xs text-slate-500">
                {row.PROFILE_EMAILID || "-"}
              </p>
            </div>
          </div>
        );
      },
    },

    {
      key: "profileCategory",
      label: "Category",
      render: (row) => (
        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
          {row.PROFILE_CATEGORY || "-"}
        </span>
      ),
    },

    {
      key: "name",
      label: "Name",
      render: (row) => (
        <TableText
          title={
            `${row.PROFILE_FIRSTNAME || ""} ${row.PROFILE_LASTNAME || ""}`.trim() ||
            "N/A"
          }
        />
      ),
    },

    // {
    //     key: "profileEmailId",
    //     label: "Email",
    //     render: (row) => (
    //         <span className="text-sm text-slate-600 dark:text-slate-300">
    //             {row.profileEmailId || "-"}
    //         </span>
    //     ),
    // },

    {
      key: "username",
      label: "Username",
      render: (row) => <TableText title={row.PROFILE_USERNAME} />,
    },

    {
      key: "location",
      label: "Location",
      render: (row) => (
        <TableText title={row.PROFILE_COUNTRY} subtitle={row.PROFILE_REGION} />
      ),
    },

    {
      key: "requestedOn",
      label: "Requested On",
      render: (row) => (
        <TableText
          title={row.CREATED_ON ? formatDisplayDate(row.CREATED_ON) : "-"}
        />
      ),
    },

    {
      key: "actions",
      label: "Actions",
      thClass:
        "px-3 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap",
      tdClass: "px-3 py-3",

      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          {/* APPROVE */}
          {!row.isDecline && (
            <button
              onClick={() => handleApprove(row)}
              disabled={row.ISCONFIRMED}
              title={row.ISCONFIRMED ? "Already Approved" : "Approve Company"}
              className={`p-2 rounded-lg transition-colors
                    ${
                      row.ISCONFIRMED
                        ? "bg-emerald-100 text-emerald-700 cursor-default dark:bg-emerald-900/30"
                        : "bg-emerald-500 hover:bg-emerald-600 text-white"
                    }`}
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          )}

          {/* DECLINE */}
          {!row.ISCONFIRMED && (
            <button
              onClick={() => handleRejectClick(row)}
              disabled={row.ISDECLINED}
              title={row.ISDECLINED ? "Already Declined" : "Decline Company"}
              className={`p-2 rounded-lg transition-colors
                    ${
                      row.ISDECLINED
                        ? "bg-red-100 text-red-700 cursor-default dark:bg-red-900/30"
                        : "bg-red-500 hover:bg-red-600 text-white"
                    }`}
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];
  // {
  //     key: "status",
  //     label: "Status",
  //     render: (row) => (
  //         <span
  //             className={`px-2.5 py-1 rounded-full text-xs font-semibold ${row.isActive
  //                 ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
  //                 : "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20"
  //                 }`}
  //         >
  //             {row.isActive ? "Active" : "Inactive"}
  //         </span>
  //     ),
  // },

  const [visibleColumns, setVisibleColumns] = useState(
    columns.map((col) => col.key),
  );

  // FILTERED COLUMNS
  const filteredColumns = useMemo(() => {
    return columns.filter((col) => visibleColumns.includes(col.key));
  }, [visibleColumns]);

  const actionLabelMap = {
    Confirmed: "Approve",
    Declined: "Decline",
    Deleted: "Delete",
  };

  const actionLabel = actionLabelMap[actionType] || actionType;

  const [showViewModal, setShowViewModal] = useState(false);

  // =========================================
  // VIEW
  // =========================================

  const handleView = (company) => {
    setSelectedCompany(company);

    setShowViewModal(true);
  };

  const handleViewCompany = (company) => {
    setSelectedCompany(company);

    navigate(`/admin/companies/${company?.COMPANYID}`, {
      state: {
        isMaster: false,
      },
    });
  };

  // =========================================
  // APPROVE
  // =========================================

  const handleApprove = (row) => {
    setSelectedCompany(row);
    setActionType("Confirmed");
    setConfirmOpen(true);
  };

  const handleRejectClick = (row) => {
    setSelectedCompany(row);
    setActionType("Declined");
    setConfirmOpen(true);
  };

  const handleDeleteClick = (row) => {
    setSelectedCompany(row);
    setActionType("Deleted");
    setConfirmOpen(true);
  };

  const handleConfirmAction = async () => {
    try {
      dispatch(callLoader(true));

      const payload = {
        COMPANYID: selectedCompany?.COMPANYID,
        ACTION_BY: "super_admin",
        REQUEST_TYPE: actionType,
      };

      const response = await dispatch(updateCompanyRequestStatus(payload));

      const success =
        response?.statuscode === 200 || response?.statuscode === 201;

      if (success) {
        const successMessages = {
          Confirmed: "Company approved successfully!",
          Declined: "Company request declined successfully!",
          Deleted: "Company deleted successfully!",
        };

        dispatch(showToast(successMessages[actionType], "success", true));

        // refresh list
        await fetchRequestCompanies({
          page: currentPage,
          search: searchTerm,
          limit: pageSize,
          forceRefresh: true,
        });

        setConfirmOpen(false);
        setSelectedCompany(null);
      } else {
        dispatch(showToast("Failed to process request", "error", true));
      }
    } catch (err) {
      console.error(err);

      dispatch(
        showToast(err?.message || "Something went wrong", "error", true),
      );
    } finally {
      dispatch(callLoader(false));
    }
  };

  // =========================================
  // FETCH
  // =========================================

  const fetchRequestCompanies = useCallback(
    async ({
      page = currentPage,
      search = searchTerm,
      limit = pageSize,
      forceRefresh = false,
    } = {}) => {
      try {
        setLoading(true);
        dispatch(callLoader(true));

        const requestPayload = {
          IPageNo: page,
          IPageRecords: limit,
          SearchKeyword: search,
          FilterType: "ALL",
        };

        const response = await dispatch(
          GetCompanyRequestList(requestPayload, forceRefresh),
        );

        console.log("Request Companies:", response);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        dispatch(callLoader(false));
      }
    },
    [currentPage, pageSize, searchTerm, dispatch],
  );

  // =========================================
  // PAGE CHANGE
  // =========================================

  useEffect(() => {
    fetchRequestCompanies({
      page: currentPage,
      limit: pageSize,
      forceRefresh: false,
    });
  }, [currentPage, pageSize]);

  // =========================================
  // SEARCH
  // =========================================

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);

      fetchRequestCompanies({
        page: 1,
        search: searchTerm,
        limit: pageSize,
        forceRefresh: true,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  return (
    <div className="space-y-5">
      {/* SEARCH */}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="relative w-full md:w-96">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="text"
              placeholder="Search signup requests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Total Requests:{" "}
            <span className="font-bold dark:text-white">
              {companyRequestListPagination?.totalRecord}
            </span>
          </div>
        </div>
        <ColumnVisibilityDropdown
          columns={columns}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          storageKey="master-request-company-table-columns"
        />
      </div>

      {/* TABLE */}

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <DataTable
          data={companyRequestList || []}
          columns={filteredColumns}
          loading={loading}
          emptyText="No signup requests found"
          Icon={Building2}
        />

        <div className="p-4">
          {/* PAGINATION */}

          <TablePagination
            currentPage={currentPage}
            totalRecords={companyRequestListPagination?.totalRecord || 0}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmAction}
        confirmText={actionLabel}
        title={`${actionLabel} Company`}
        message={
          <>
            Are you sure you want to{" "}
            <span className="font-semibold">{actionLabel?.toLowerCase()}</span>{" "}
            the request for{" "}
            <span className="font-bold italic text-slate-900 dark:text-white">
              {selectedCompany?.COMPANYNAME}
            </span>
            ?
          </>
        }
        confirmBtn={actionType}
      />
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase text-slate-400 mb-1">{label}</p>

    <p className="text-sm font-medium text-slate-800 dark:text-slate-200 break-words">
      {value || "-"}
    </p>
  </div>
);

export default RequestCompanies;
